import React, { useState, useRef } from "react";
import { useTable } from "@tanstack/react-table";
import { ReactTableColumnDef, reactTableFeatures } from "../../../components/src/react-table/features";

interface Person {
    id: number;
    name: string;
    age: number;
}

const initialData: Person[] = [
    { id: 1, name: "Alice", age: 25 },
    { id: 2, name: "Bob", age: 30 },
    { id: 3, name: "Charlie", age: 35 },
    { id: 4, name: "Dave", age: 40 },
    { id: 5, name: "Eve", age: 45 }
];

export function TestPage(): React.ReactElement {
    const [data] = useState(initialData);
    const [selectedRowIds, setSelectedRowIds] = useState<Record<number, boolean>>({});
    const [isDragging, setIsDragging] = useState(false);
    const dragStartRef = useRef<number | null>(null);

    const columns: ReactTableColumnDef<Person>[] = [
        {
            accessorKey: "name",
            header: "Name",
            cell: (info) => info.getValue()
        },
        {
            accessorKey: "age",
            header: "Age",
            cell: (info) => info.getValue()
        }
    ];

    const table = useTable({
        features: reactTableFeatures,
        data,
        columns
    });

    const toggleRowSelection = (id: number) => {
        setSelectedRowIds((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const handleDragStart = (startIndex: number) => {
        dragStartRef.current = startIndex;
        setIsDragging(true);
    };

    const handleDragEnd = () => {
        setIsDragging(false);
        dragStartRef.current = null;
    };

    const handleMouseOverRow = (currentIndex: number) => {
        if (isDragging && dragStartRef.current !== null) {
            const start = Math.min(dragStartRef.current, currentIndex);
            const end = Math.max(dragStartRef.current, currentIndex);
            const updatedSelection = { ...selectedRowIds };
            table.getRowModel().rows.forEach((row, index) => {
                const rowId = row.index + 1;
                updatedSelection[rowId] = index >= start && index <= end;
            });
            setSelectedRowIds(updatedSelection);
        }
    };

    const handlePrintSelectedRows = () => {
        const selectedNames = data.filter((row) => selectedRowIds[row.id]).map((row) => row.name);
        console.log("Selected Names:", selectedNames);
    };

    return (
        <div className="p-6">
            <div className="mb-4 p-4 bg-white shadow rounded-lg">
                <table className="w-full border-collapse" onMouseUp={handleDragEnd}>
                    <thead>
                        <tr>
                            <th className="border-b p-2 text-left">Select</th>
                            <th className="border-b p-2 text-left">Name</th>
                            <th className="border-b p-2 text-left">Age</th>
                        </tr>
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map((row, index) => {
                            const rowId = row.index + 1;
                            const isSelected = !!selectedRowIds[rowId];
                            return (
                                <tr
                                    key={row.id}
                                    className={`hover:bg-gray-100 ${isSelected ? "bg-sky-100" : ""}`}
                                    onMouseDown={() => handleDragStart(index)}
                                    onMouseOver={() => handleMouseOverRow(index)}
                                >
                                    <td className="border-b p-2">
                                        <input type="checkbox" checked={isSelected} onChange={() => toggleRowSelection(rowId)} />
                                    </td>
                                    {row.getVisibleCells().map((cell) => (
                                        <td key={cell.id} className="border-b p-2">
                                            {cell.getValue() as string}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <button className="mt-4 px-4 py-2 bg-sky-600 text-white rounded-lg" onClick={handlePrintSelectedRows}>
                    Print Selected Names
                </button>
            </div>
        </div>
    );
}
